import {ChangeEvent, useEffect, useId, useRef, useState} from 'react';
import {
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Box,
  InputLabel,
  Checkbox,
  IconButton,
} from '@mui/material';
import Select, {SelectChangeEvent, SelectProps} from '@mui/material/Select';
import {styled} from '@mui/material/styles';

import AurSelectedItemsPanel from './AurSelectedItemsPanel';
import AurButton from '../aur-button/AurButton';
import SelectItem from './SelectItem';
import {ISelectItem} from './types';
import {ReactComponent as SearchIcon} from '../../../assests/svg/search-icon-right.svg';
import {ReactComponent as ExpandIcon} from '../../../assests/svg/down-triangle.svg';
import {ReactComponent as ClearIcon} from '../../../assests/svg/clear-icon.svg';

import {t} from 'i18next';
import styles from './style.module.scss';
import {getFirstFocusableElement} from '../../../utils/utils';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';
import { DropdownIcon } from '../aur-single-select-dropdown/AurSingleSelectDropdown';

const SCROLL_BAR_STYLE_CLASS = 'aur-item-container-scroll'; // Scrollbar selector class
const ITEMS_WITHOUT_SCROLL = 5; // Default items/options panel height
const ITEM_HEIGHT = 56; // Single item/option height
const DEFAULT_MUI_BORDER = 2; // default border (top + bottom) for dropdown menu provided by the MUI Library
const MUI_CANVAS_ADJUSTMENT = 44; // default bottom-padding applied by MUI Library
const CANVAS_UPPER_ADJUSTMENT = 8; // adjust the dropdown position to upper direction by addning extra padding/margin

// FormControl fixed style
export const StyledFormControl = styled(FormControl)(({theme}) => ({
  '& .MuiFilledInput-root': {
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.default,
    },
  },
  backgroundColor: theme.palette.background.paper,
}));
// MenuProps fixed style
export const MenuProps = {
  PaperProps: {
    classes: {
      root: `${styles.multiSelectDropdownMenu}`,
    },
  },
  disableScrollLock: true, // Prevent the scrollbar from disappearing
};

type AurSelectChangeEvent<Value = string> =
  | (Event & {
      target: {
        value: Value;
        name: string;
      };
    })
  | ChangeEvent<HTMLInputElement>;

type AurSelectDropdownProps = {
  items: ISelectItem[]; // All options/items
  itemKey: string; // Name of the unique property of item/option object
  itemDisplay: string; // Name of the "display text in options" property of item/option object
  dropdownLabel?: string; // Label of the dropdown
  search?: boolean;
  searchPlaceHolder?: string; // Search field place holder
  selectAll?: boolean;
  selectAllLabel?: string; // Select all checkbox label
  defaultSelection?: ISelectItem[]; // Default selected items/options
  itemsCountwithNoScroll?: number; // Minimum items/option shows with no scrollbar appear
  submitButtonText?: string;
  disabled?: boolean;
  helperTextClass?: string;
  showCheckIcon?: boolean;

  // For state
  field?: {
    value: ISelectItem[] | undefined;
    onChange: (event: SelectChangeEvent<string[] | unknown>) => void;
    onBlur: () => void;
  };
  fieldState?: {
    error?: {
      message?: string;
    };
  };

  // For validation
  hasValidationError?: boolean;
  validationText?: string;
  helperText?: string;
  labelId?: string;
  dataTestId?: string;
  onSelectionChange: (selectedItems?: ISelectItem[]) => void; // Called after items/options commit/add
  onClearClicked?: () => void; // Called after clearing all selected items/options
  onOpenDropdown?: () => void; // Called after dropdown onen
  onCloseDropdown?: () => void; // Called after dropdown close
  clearValues?: boolean;
  blurOnFocusOut?: boolean;
  hintText?: string;
} & SelectProps;

/**
 * Custom multi select dropdown
 *
 * Important: the container around this component must have >= 16px left padding/margin [mui suggested standard]
 */
const AurSelectDropdown = (props: AurSelectDropdownProps) => {
  const {
    items,
    itemKey,
    itemDisplay,
    dropdownLabel,
    search = true,
    searchPlaceHolder,
    selectAll = true,
    selectAllLabel,
    defaultSelection = [],
    itemsCountwithNoScroll = ITEMS_WITHOUT_SCROLL,
    submitButtonText = t('common.apply'),
    disabled = false,
    helperTextClass,

    field,
    fieldState,

    hasValidationError = false,
    validationText = '',
    helperText = '',
    dataTestId,
    onSelectionChange,
    onClearClicked,
    onOpenDropdown,
    onCloseDropdown,
    clearValues = false,
    showCheckIcon = true,
    blurOnFocusOut,
    hintText,
    ...rest
  } = props;

  const [itemData, setItemData] = useState<ISelectItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<ISelectItem[]>([]);
  const [selectedItemsTemp, setSelectedItemsTemp] = useState<ISelectItem[]>([]);
  const [allSelect, setAllSelect] = useState<'check' | 'partialCheck' | ''>('');
  const [showSelectAllCheckbox, setShowSelectAllCheckbox] = useState(true);
  const menuItemRefs = useRef<{[key: string]: HTMLLIElement | null}>({});

  const itemsLengthRef = useRef<number>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchFieldText, setSearchFieldText] = useState('');
  const [mouseEnter, setMouseEnter] = useState(false);
  const hintId = `aur-select-hint-${useId().replace(/:/g, '')}`;
  const titleLabelId = props.labelId ?? 'aur-select-dropdown-label';
  const menuId = `aur-select-menu-${useId().replace(/:/g, '')}`;
  const [menuPosition, setMenuPosition] = useState(0);

  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: styles.tabFocusedBorder,
    mouseFocusClass: styles.mouseFocusedBorder,
  });

  // Set items/options panel height
  let itemContainerMaxHeight = ITEM_HEIGHT * itemsCountwithNoScroll;
  if (itemData.length < itemsCountwithNoScroll)
    itemContainerMaxHeight = ITEM_HEIGHT * itemData.length;

  // create new custom event
  const createSelectChangeEvent = (
    Value: ISelectItem[],
    Name?: string
  ): AurSelectChangeEvent<ISelectItem[]> => {
    return {
      target: {
        value: Value,
        name: Name,
      },
      type: 'SelectChangeEvent',
      currentTarget: null,
      bubbles: false,
      cancelable: false,
      timeStamp: Date.now(),
    } as AurSelectChangeEvent<ISelectItem[]>;
  };

  // Called for temporary selected/unselected
  const selectionChangeHandler = (data: ISelectItem) => {
    const targetItem = selectedItemsTemp.find(
      (item) => item[itemKey] == data[itemKey]
    );
    setIsExpanded(false);
    if (targetItem) {
      setSelectedItemsTemp((pv) => {
        return [...pv.filter((item) => item[itemKey] != data[itemKey])];
      });
    } else {
      setSelectedItemsTemp((pv) => {
        return [...pv, data];
      });
    }
  };

  const searchClearHandler = () => {
    setSearchFieldText('');
    setItemData(items);
    setShowSelectAllCheckbox(true);
  };

  // Called every key press on search field
  const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    // On clear scroll to first item/option
    if (!event.target.value.length) {
      setItemData(items);
      setShowSelectAllCheckbox(true);
    }
    setSearchFieldText(event.target.value);
    const searchText = event.target.value.trim();
    if (!searchText) return;
    // filter out items does not match searchtext
    const filterdItems = filterAndSortItems(items, searchText, itemDisplay);

    if (filterdItems && filterdItems.length > 0) {
      setShowSelectAllCheckbox(true);
    } else {
      setShowSelectAllCheckbox(false);
    }

    setItemData(filterdItems);
    SetAllSelectCheckbox();
  };

  function filterAndSortItems(
    items: ISelectItem[],
    searchText: string,
    itemDisplay: string
  ): ISelectItem[] {
    const searchStr = searchText.toLowerCase();

    return items
      .map((item) => {
        const displayValue = String(item[itemDisplay] ?? '').toLowerCase();

        if (displayValue === searchStr) {
          return {item, rank: 0};
        } else if (displayValue.startsWith(searchStr)) {
          return {item, rank: 1};
        } else if (displayValue.includes(searchStr)) {
          return {item, rank: 2};
        } else {
          return null;
        }
      })
      .filter(
        (entry): entry is {item: ISelectItem; rank: number} => entry !== null
      )
      .sort((a, b) => a.rank - b.rank)
      .map((entry) => entry.item);
  }

  // Called for "Clear All" button
  const clearAllHandler = () => {
    const event: AurSelectChangeEvent<ISelectItem[]> = createSelectChangeEvent(
      []
    );

    field?.onChange(event);

    setSelectedItemsTemp([]);
    setSelectedItems([]);
    setAllSelect('');
    if (onClearClicked) onClearClicked();
  };

  // Called for "Add" button
  const addClickHandler = () => {
    const event: AurSelectChangeEvent<ISelectItem[]> = createSelectChangeEvent([
      ...selectedItemsTemp,
    ]);

    field?.onChange(event);

    setSelectedItems([...selectedItemsTemp]);
    onSelectionChange([...selectedItemsTemp]);
    setIsDropdownOpen(false);
    setSearchFieldText('');
  };

  // Called after dropdown open
  const selectOpenHandler = () => {
    setIsDropdownOpen(true);
    setSelectedItemsTemp([...selectedItems]);
    if (onOpenDropdown) onOpenDropdown();
  };
  // Called after dropdown close
  const selectCloseHandler = () => {
    if (onCloseDropdown) onCloseDropdown();
    setIsDropdownOpen(false);
    setTimeout(() => {
      searchClearHandler();
      if (blurOnFocusOut) {
        selectComponentRef.current?.blur();
        field?.onBlur?.(); // mark touched for RHF
      }
    }, 200);
  };

  // Called for "Select All" checkbox button
  const selectAllHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setAllSelect((pv) => {
      if (pv == 'check') {
        if (itemData.length == items.length) setSelectedItemsTemp([]);
        else {
          const serialize = (item: ISelectItem) =>
            JSON.stringify(Object.entries(item).sort());

          const itemDataSet = new Set(itemData.map(serialize));

          const filteredSelectedItems = selectedItemsTemp.filter(
            (item) => !itemDataSet.has(serialize(item))
          );

          setSelectedItemsTemp(filteredSelectedItems);
        }
        return '';
      }
      if (pv == '' || pv == 'partialCheck') {
        if (itemData.length == items.length) {
          setSelectedItemsTemp([...itemData]);
        } else {
          setSelectedItemsTemp(
            mergeWithoutDuplicates(selectedItemsTemp, itemData)
          );
        }
        return 'check';
      }
      return '';
    });
  };
  // Initial state setup
  useEffect(() => {
    setSelectedItems([...defaultSelection]);
    setSelectedItemsTemp([...defaultSelection]);
    if (!searchFieldText) {
      setItemData(items);
      itemsLengthRef.current = items.length;
    }
    calculateDirection();
  }, [items, defaultSelection]);

  // Invoke for temporary selection change

  const SetAllSelectCheckbox = () => {
    if (selectedItemsTemp.length == items.length && items.length > 0)
      setAllSelect('check');
    else if (
      itemData.length < items.length &&
      selectedItemsTemp.length > 0 &&
      isSubset(selectedItemsTemp, itemData)
    )
      setAllSelect('check');
    else if (
      selectedItemsTemp.length > 0 &&
      hasAnyMatch(itemData, selectedItemsTemp)
    ) {
      setAllSelect('partialCheck');
    } else setAllSelect('');
  };

  interface ISelectItem {
    [key: string]: string | number;
  }

  const mergeWithoutDuplicates = (
    existing: ISelectItem[],
    incoming: ISelectItem[]
  ): ISelectItem[] => {
    const serialize = (item: ISelectItem) =>
      JSON.stringify(Object.entries(item).sort());

    const seen = new Set(existing.map(serialize));

    const filteredIncoming = incoming.filter((item) => {
      const key = serialize(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return [...existing, ...filteredIncoming];
  };

  const hasAnyMatch = (
    itemData: ISelectItem[],
    selectedItems: ISelectItem[]
  ): boolean => {
    const serialize = (item: ISelectItem) =>
      JSON.stringify(Object.entries(item).sort());

    const selectedSet = new Set(selectedItems.map(serialize));

    return itemData.some((item) => selectedSet.has(serialize(item)));
  };

  const isSubset = (
    itemData: ISelectItem[],
    selectedItems: ISelectItem[]
  ): boolean => {
    const serialize = (item: ISelectItem) =>
      JSON.stringify(Object.entries(item).sort());

    const itemDataSet = new Set(itemData.map(serialize));

    for (const selected of selectedItems) {
      const serialized = serialize(selected);
      if (!itemDataSet.has(serialized)) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    calculateDirection();
  }, [itemData]);

  useEffect(() => {
    SetAllSelectCheckbox();
  }, [selectedItemsTemp, itemData]);

  useEffect(() => {
    if (clearValues) clearAllHandler();
  }, [clearValues]);

  const popUpContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isDropdownOpen) {
      setTimeout(() => {
        const firstElement = getFirstFocusableElement(
          popUpContainerRef.current
        );
        firstElement?.focus();
      }, 100);
    }
  }, [isDropdownOpen]);

  /*
    This section below handles the calculation for
    dropdown menu position based on available space
  */
  const [calculatedDropdownHeight, setCalculatedDropdownHeight] =
    useState<number>(0);

  const conditionalPositioningProp =
    menuPosition < 0
      ? {marginTop: `${menuPosition}px`}
      : {paddingTop: `${menuPosition}px`};

  const selectComponentRef = useRef<HTMLInputElement | null>(null);

  // calculates the amount of mergin needed to adjust the dropdown menu position
  const calculateDirection = () => {
    if (!selectComponentRef.current) return;

    const rect = selectComponentRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow =
      window.innerHeight -
      rect.bottom -
      selectComponentRef.current.clientHeight;

    const dropdownHeight =
      popUpContainerRef.current?.parentElement?.clientHeight ??
      0 + DEFAULT_MUI_BORDER;

    const selectComponentHeight =
      selectComponentRef.current.clientHeight + DEFAULT_MUI_BORDER;

    if (popUpContainerRef.current?.clientHeight)
      setCalculatedDropdownHeight(
        dropdownHeight + selectComponentHeight + MUI_CANVAS_ADJUSTMENT
      );

    if (spaceBelow >= spaceAbove || spaceBelow >= dropdownHeight)
      setMenuPosition(selectComponentHeight);
    else setMenuPosition((dropdownHeight + CANVAS_UPPER_ADJUSTMENT) * -1);
  };

  // Observe window resize
  useEffect(() => {
    // Run on mount and scroll/resize
    window.addEventListener('scroll', calculateDirection, true);
    window.addEventListener('resize', calculateDirection);

    return () => {
      window.removeEventListener('scroll', calculateDirection, true);
      window.removeEventListener('resize', calculateDirection);
    };
  }, []);
  ///////////////////////////////////////////

  const handleKeyDownOfListItem = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape') event.stopPropagation();
  };

  const handleCellKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent the default Enter behavior
      setAllSelect((pv) => {
        if (pv == 'check') {
          setSelectedItemsTemp([]);
          return '';
        }
        if (pv == '' || pv == 'partialCheck') {
          setSelectedItemsTemp([...itemData]);
          return 'check';
        }
        return '';
      });
    }
  };

  const hasError = () => {
    if (hasValidationError) {
      return true;
    }
    if (fieldState?.error?.message) {
      return true;
    }
    return false;
  };

  return (
    <StyledFormControl
      error={hasError()}
      variant="filled"
      fullWidth
      className={`${styles.multiSelectDropdownInput} ${borderClass}`}
    >
      <span id={hintId} style={{display: 'none'}}>
        {hintText ?? t('common.dropdown.optionSelectHintsText')}
      </span>
      <InputLabel
        id={titleLabelId}
        className={`${disabled ? 'Mui-disabled' : ''}`}
      >
        {dropdownLabel}
      </InputLabel>

      <Select
        ref={selectComponentRef}
        fullWidth
        multiple
        value={selectedItems}
        MenuProps={{
          ...MenuProps,
          MenuListProps: {
            id: menuId,
          },
          disablePortal: true,
          anchorReference: 'none',
          sx: {
            position: 'absolute',
            height: `${calculatedDropdownHeight}px`,
            ...conditionalPositioningProp,
          },
          TransitionProps: {
            onEnter: calculateDirection,
          },
        }}
        IconComponent={DropdownIcon}
        labelId={titleLabelId}
        disabled={disabled}
        open={isDropdownOpen}
        onBlur={field?.onBlur}
        onOpen={selectOpenHandler}
        onClose={selectCloseHandler}
        data-testid={dataTestId}
        renderValue={() => (
          <AurSelectedItemsPanel
            selectedItems={selectedItems}
            itemDisplay={itemDisplay}
          />
        )}
        aria-describedby={hintId}
        aria-haspopup="true"
        {...rest}
        onFocus={focusHandler}
        onMouseDown={mouseKeyDownHandler}
      >
        <Box ref={popUpContainerRef} tabIndex={0}>
          {(search || selectAll) && (
            <Box onKeyDown={handleKeyDownOfListItem} tabIndex={-1}>
              {search && (
                <Box className={`${styles.aurSelectSearchContainer}`}>
                  <TextField
                    fullWidth
                    onChange={searchChangeHandler}
                    id="outlined-basic"
                    placeholder={searchPlaceHolder}
                    variant="outlined"
                    className={`${styles.aurMultiSelectSearch}`}
                    value={searchFieldText}
                    InputProps={{
                      endAdornment: searchFieldText.length ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear"
                            size="small"
                            onClick={() => searchClearHandler()}
                          >
                            <ClearIcon aria-hidden="true" focusable={false} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        <InputAdornment position="end">
                          <IconButton
                            tabIndex={-1}
                            size="small"
                            disableFocusRipple
                            disableRipple
                            disabled
                          >
                            <SearchIcon aria-hidden="true" focusable={false} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
              {selectAll && (
                <Box className={`${styles.aurSelectFooterContainer}`}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Box
                        tabIndex={0}
                        onClick={selectAllHandler}
                        onKeyDown={handleCellKeyDown}
                        sx={{display: showSelectAllCheckbox ? 'block' : 'none'}}
                      >
                        <Checkbox
                          className={styles.aurSelectItemCheckBox}
                          inputProps={{
                            tabIndex: -1,
                          }}
                          tabIndex={-1}
                          aria-hidden={true}
                          checked={allSelect == 'check'}
                          indeterminate={allSelect == 'partialCheck'}
                          checkedIcon={
                            <img
                              src={
                                mouseEnter
                                  ? '/assests/images/checked_checkbox_hover.svg'
                                  : '/assests/images/checked_checkbox.svg'
                              }
                            />
                          }
                          onMouseEnter={() => setMouseEnter(true)}
                          onMouseLeave={() => setMouseEnter(false)}
                          indeterminateIcon={
                            <img
                              src={
                                mouseEnter
                                  ? '/assests/images/indeterminate_checkbox_hover.svg'
                                  : '/assests/images/indeterminate_checkbox.svg'
                              }
                            />
                          }
                        />
                        <Typography className={`${styles.aurAllSelectLabel}`}>
                          {selectAllLabel}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Box sx={{display: showSelectAllCheckbox ? 'block' : 'none'}}>
                <Divider className={`${styles.aurMultiSelectPanelDivider}`} />
              </Box>
            </Box>
          )}
          {itemData.length > 0 && (
            <>
              <Box
                sx={{
                  // These styles are dynamic and based on items/options data
                  height: `${itemData.length * ITEM_HEIGHT}px`,
                  overflow: 'auto',
                  maxHeight: `${itemContainerMaxHeight}px`,
                }}
                className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar}`}
              >
                {itemData.map((item) => {
                  return (
                    <SelectItem
                      ref={(el) => (menuItemRefs.current[item[itemKey]] = el)}
                      key={item[itemKey]}
                      itemData={item}
                      itemDisplay={itemDisplay}
                      onItemClick={selectionChangeHandler}
                      isExpanded={isExpanded}
                      isSelected={selectedItemsTemp.some(
                        (sit) => sit[itemKey] == item[itemKey]
                      )}
                      showCheckIcon={showCheckIcon}
                      onKeyDown={handleKeyDownOfListItem}
                    />
                  );
                })}
              </Box>
              <Divider className={`${styles.aurMultiSelectPanelDivider}`} />
              <Box className={`${styles.aurSelectFooterContainer}`}>
                <Grid container>
                  <Grid item xs={6}>
                    <Box className={'left-align-center-y'}>
                      <AurButton
                        tabIndex={0}
                        type="text"
                        title={t('searchableDropdown.clearAll')}
                        label={t('searchableDropdown.clearAll')}
                        size="Medium"
                        disabled={selectedItemsTemp.length === 0}
                        onClick={() => clearAllHandler()}
                        dataTestId="cancel-button"
                        onKeyDown={handleKeyDownOfListItem}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box className={'right-align-center-y'}>
                      <AurButton
                        type="continue"
                        tabIndex={0}
                        title={submitButtonText}
                        label={`${submitButtonText} ${selectedItemsTemp.length || ''}`}
                        size="Medium"
                        disabled={selectedItemsTemp.length === 0}
                        onClick={() => addClickHandler()}
                        dataTestId="Apply-button"
                        onKeyDown={handleKeyDownOfListItem}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Box>
      </Select>
      <FormHelperText className={fieldState?.error?.message && 'label-error'}>
        {fieldState?.error?.message}
      </FormHelperText>
      <Box className={helperTextClass}>
        <FormHelperText>
          {hasValidationError ? validationText : helperText}
        </FormHelperText>
      </Box>
    </StyledFormControl>
  );
};

export default AurSelectDropdown;
